import React from 'react';
import { format, parse } from 'date-fns';
import get from 'lodash.get';
import findLastIndex from 'lodash.findlastindex';
import { TextCell } from '../../../../components/cell/shared/Cells';
import { getUrlParams } from '../../../../utils/shared/urlUtils';
import { applyLeftBorder, applyRightBorder } from '../../../../utils/shared/cellUtils';
import {
    getGradeTitle,
    GRADE as GRADE_CONSTANT,
} from '../../../../constants/shared/GradesConstants';
import { getStudentResultByMeasureYearGradePeriod } from '../../../../utils/shared/filterUtils';
import {
    getMeasureAvailiabilityStartIndex,
    isMeasureAvailableInGrade,
} from '../../../../utils/shared/gradeToMeasureUtils';
import { CELL_TYPE } from '../../../../constants/shared/CellConstants';
import { MEASURE as SHARED_MEASURE } from '../../../../constants/shared/MeasuresConstants';
import {
    buildColSpan,
    isMeasureEnabledForSpecificMapper,
} from '../../../../utils/shared/tableUtils';
import { PERIOD as PERIOD_CONSTANT } from '../../../../constants/shared/CommonConstants';

const { OVERALL } = SHARED_MEASURE;

// these constants were created to make easier build cells & colSpans
export const CELL_MODES = {
    EMPTY: 'empty',
    SCORE: 'score',
    GRADE: 'grade',
    PERIOD: 'period',
    MEASURE: 'measure',
};
const { EMPTY, MEASURE, PERIOD, SCORE, GRADE } = CELL_MODES;

/**
 * Get array of objects which contains
 * all neccessary data for handling measure's cell position.
 */
export const getMeasuresStart = (startedMeasures, DefaultGradeToMeasureMapper) =>
    startedMeasures.map(({ key }) => {
        const measureMapperObject = DefaultGradeToMeasureMapper.find(({ measures }) =>
            measures.includes(key),
        );

        return { key, grade: measureMapperObject.key };
    });

const getRowNumber = measureIndex => {
    const SS_ROW_COUNT = 2;
    return measureIndex === 0 ? SS_ROW_COUNT : measureIndex * SS_ROW_COUNT + SS_ROW_COUNT;
};

export const formatYear = year => {
    const formattedYear = format(parse(year.toString(), 'yyyy', new Date()), `''yy`);
    return `${formattedYear}`;
};

export const getBorderClassNames = (
    gradeYearIndex,
    lastGradeYearIndex,
    periodIndex,
    lastPeriodIndex,
) => {
    const leftBorderClassName = applyLeftBorder(periodIndex === 0);
    const rightBorderClassName = applyRightBorder(
        lastGradeYearIndex === gradeYearIndex && lastPeriodIndex === periodIndex,
    );

    return `${leftBorderClassName} ${rightBorderClassName}`;
};

export const measureGradeColSpanIndexes = (startColSpanIndex, toyPeriods = []) => {
    const toyPeriodsLength = toyPeriods.length;
    const columnStopIndex = startColSpanIndex + toyPeriodsLength - 1;

    return { columnStartIndex: startColSpanIndex, columnStopIndex };
};

const getResultObjectColSpanProps = (
    grade,
    measure,
    gradeYears,
    gradeYearIndex,
    startedMeasures,
    lastGradeYearIndex,
    historicalGradeToMeasuresMapper,
) => {
    const { measureStartIndex } = getMeasureAvailiabilityStartIndex(
        historicalGradeToMeasuresMapper,
        measure.key,
    );
    const measureStart = getMeasuresStart(startedMeasures, historicalGradeToMeasuresMapper);
    const isRetainingSequenceStart =
        gradeYears.findIndex(gradeYear => gradeYear.grade === grade) === gradeYearIndex;
    const measureKeyFormatter = (key, name) => (key === OVERALL ? '' : `(${name})`);

    const title =
        measureStart.find(
            measureObject => measureObject.key === measure.key && measureObject.grade === grade,
        ) && isRetainingSequenceStart
            ? `${measure.title} ${measureKeyFormatter(measure.key, measure.name)}`
            : null;

    const leftBorderClassName =
        gradeYearIndex === 0 || measureStartIndex === gradeYearIndex ? 'dark-left-border' : '';
    const rightBorderClassName = applyRightBorder(gradeYearIndex === lastGradeYearIndex);

    return {
        title,
        classNames: `${leftBorderClassName} ${rightBorderClassName}`,
    };
};

export const getHeaderObjects = (
    gradeYears,
    toyPeriods,
    summerPeriodLabel = PERIOD_CONSTANT.SUMMER,
    isLouisianaUser = false,
) => {
    const rowsObjects = [];
    const colSpansObjects = [];
    const lastGradeYearIndex = findLastIndex(gradeYears);
    const emptyRowCells = { cellMode: EMPTY, cellsObjects: [] };
    const periodRowCells = { isGridHeader: true, cellMode: PERIOD, cellsObjects: [] };
    let startColSpanIndex = 0;

    gradeYears.forEach(({ grade, year }, gradeYearIndex) => {
        const toys = toyPeriods.filter(
            period =>
                !(isLouisianaUser && grade !== GRADE_CONSTANT.THIRD_GRADE) ||
                period !== PERIOD_CONSTANT.SUMMER,
        );

        const lastPeriodIndex = findLastIndex(toys);
        const colSpanIndexes = measureGradeColSpanIndexes(startColSpanIndex, toys);
        startColSpanIndex = colSpanIndexes.columnStopIndex + 1;
        colSpansObjects.push({
            colSpanMode: GRADE,
            isRightBorder: lastGradeYearIndex === gradeYearIndex,
            colSpanIndexes,
            title: `${getGradeTitle(grade)}: ${formatYear(year - 1)}-${formatYear(year)}`,
        });

        toys.forEach((period, periodIndex) => {
            const uniqueKey = grade + year + period;
            const classNames = getBorderClassNames(
                gradeYearIndex,
                lastGradeYearIndex,
                periodIndex,
                lastPeriodIndex,
            );

            emptyRowCells.cellsObjects.push({ uniqueKey });
            periodRowCells.cellsObjects.push({
                classNames,
                grade,
                year,
                period: period === PERIOD_CONSTANT.SUMMER ? summerPeriodLabel : period,
                uniqueKey,
            });
        });
    });

    rowsObjects.push(emptyRowCells, periodRowCells);

    return { rowsObjects, colSpansObjects };
};

export const getResultsObjects = ({
    measures,
    gradeYears,
    interps,
    statuses,
    historicalGradeToMeasuresMapper,
    getGoalByMeasureGradePeriod,
    startedMeasures,
    toyPeriods,
    getSpecificGoal = () => null,
    isLouisianaUser,
}) => {
    const rowsObjects = [];
    const colSpansObjects = [];
    const lastGradeYearIndex = findLastIndex(gradeYears);

    measures.forEach((measure, measureIndex) => {
        const scoreRowCells = {
            cellMode: SCORE,
            cellsObjects: [],
        };
        const emptyRowCells = {
            cellMode: EMPTY,
            cellsObjects: [],
        };

        const measureKey = measure.key;
        const row = getRowNumber(measureIndex);

        let startColSpanIndex = 0;
        gradeYears.forEach(({ grade, year }, gradeYearIndex) => {
            const toys = toyPeriods.filter(
                period =>
                    !(isLouisianaUser && grade !== GRADE_CONSTANT.THIRD_GRADE) ||
                    period !== PERIOD_CONSTANT.SUMMER,
            );

            const lastPeriodIndex = findLastIndex(toys);
            const isMeasureAvailable = isMeasureAvailableInGrade(
                historicalGradeToMeasuresMapper,
                grade,
                measureKey,
            );

            const colSpanIndexesObj = measureGradeColSpanIndexes(startColSpanIndex, toys);
            if (isMeasureAvailable) {
                const colSpan = {
                    row,
                    colSpanMode: MEASURE,
                    colSpanIndexes: colSpanIndexesObj,
                    ...getResultObjectColSpanProps(
                        grade,
                        measure,
                        gradeYears,
                        gradeYearIndex,
                        startedMeasures,
                        lastGradeYearIndex,
                        historicalGradeToMeasuresMapper,
                    ),
                };

                colSpansObjects.push(colSpan);
            }

            toys.forEach((period, periodIndex) => {
                if (!isMeasureAvailable) {
                    const colSpanIndexes = {
                        columnStartIndex: startColSpanIndex,
                        columnStopIndex: startColSpanIndex,
                    };
                    startColSpanIndex += 1;
                    const borderClassNames = getBorderClassNames(
                        gradeYearIndex,
                        lastGradeYearIndex,
                        periodIndex,
                        lastPeriodIndex,
                    );

                    colSpansObjects.push({
                        row,
                        classNames: borderClassNames,
                        colSpanIndexes,
                        colSpanMode: EMPTY,
                    });
                }

                const uniqueKey = grade + year + period;
                const goal = getGoalByMeasureGradePeriod(
                    interps,
                    measureKey,
                    grade,
                    period,
                    getSpecificGoal(measureKey, grade, period),
                );

                const status = getStudentResultByMeasureYearGradePeriod(
                    statuses,
                    measureKey,
                    year,
                    grade,
                    period,
                );
                const isMeasureApplicable = isMeasureEnabledForSpecificMapper(
                    measureKey,
                    grade,
                    period,
                    historicalGradeToMeasuresMapper,
                );
                const classNames = getBorderClassNames(
                    gradeYearIndex,
                    lastGradeYearIndex,
                    periodIndex,
                    lastPeriodIndex,
                );
                const scoreRow = {
                    year,
                    goal,
                    grade,
                    period,
                    status,
                    uniqueKey,
                    measureKey,
                    classNames,
                    gradeYearIndex,
                    isMeasureAvailable,
                    hasBottomSpace: true,
                    isGoalAvailable: isMeasureApplicable,
                    isAdditionalPanel: get(status, 'isWebEntry', false),
                };

                emptyRowCells.cellsObjects.push({ uniqueKey });
                scoreRowCells.cellsObjects.push(scoreRow);
            });
            startColSpanIndex = colSpanIndexesObj.columnStopIndex + 1;
        });
        rowsObjects.push(emptyRowCells, scoreRowCells);
    });

    return { rowsObjects, colSpansObjects };
};

const buildEmptyColSpan = (row, classNames, emptyColSpanIndexes) =>
    buildColSpan(
        row,
        emptyColSpanIndexes,
        <TextCell classNames={`ss-empty-col-span ${classNames}`} isDataCell={false} />,
        { zIndex: 0 },
    );

const buildMeasureColSpan = (row, classNames, title, measureColSpanIndexes) =>
    buildColSpan(
        row,
        measureColSpanIndexes,
        <TextCell
            title={title}
            content={title}
            classNames={`ss-measure-col-span ${classNames}`}
            isDataCell={false}
        />,
        { zIndex: 0 },
    );

const buildGradeColSpan = (isRightBorder, title, gradeColSpanIndexes) => {
    const FIRST_ROW_INDEX = 0;

    return buildColSpan(
        FIRST_ROW_INDEX,
        gradeColSpanIndexes,
        <TextCell
            content={title}
            classNames={`ss-grade-col-span dark-left-border ${applyRightBorder(isRightBorder)}`}
            isDataCell={false}
        />,
        { zIndex: 1 },
    );
};

export const getRowsToRender = (
    benchmarkHistoryData,
    openProbeDetails,
    CommonCellBuilder,
    location,
    DefaultGradeToMeasureMapper,
) => {
    const { colSpansObjects, rowsObjects } = benchmarkHistoryData;
    const rows = [];

    rowsObjects.forEach(rowObject => {
        const { cellMode, cellsObjects, isGridHeader = false } = rowObject;
        const cellsPerRow = {};

        cellsObjects.forEach(cell => {
            const {
                goal,
                grade,
                status,
                period,
                uniqueKey,
                measureKey,
                classNames,
                hasBottomSpace,
                isGoalAvailable,
                isAdditionalPanel,
                isMeasureAvailable,
            } = cell;

            const cellsBuilder = {
                [EMPTY]: (
                    <TextCell classNames="ss-period-cell dark-left-border" isDataCell={false} />
                ),
                [PERIOD]: (
                    <TextCell
                        content={period}
                        isDataCell={false}
                        classNames={`ss-period-cell ${classNames}`}
                    />
                ),
                [SCORE]: (
                    <CommonCellBuilder
                        grade={grade}
                        period={period}
                        location={location}
                        measure={measureKey}
                        statusForMeasureYearPeriod={status}
                        externalId={getUrlParams(location).studentId}
                        cellType={CELL_TYPE.STUDENT_SUMMARY}
                        gatingMeasureCellType={CELL_TYPE.SS_GATING_MEASURE}
                        openPanel={get(status, 'resultId') ? openProbeDetails : () => {}}
                        optionalCellData={{
                            goal,
                            classNames: measureKey
                                ? `${measureKey.toLowerCase()}-cell ${classNames}`
                                : classNames,
                            hasBottomSpace,
                            isGoalAvailable,
                            isAdditionalPanel,
                            isMeasureAvailable,
                        }}
                        isCellDisabled={
                            !isMeasureEnabledForSpecificMapper(
                                measureKey,
                                grade,
                                period,
                                DefaultGradeToMeasureMapper,
                            )
                        }
                    />
                ),
            };

            cellsPerRow[uniqueKey] = cellsBuilder[cellMode];
        });

        // `isGridHeader` prop makes period cells sticky
        rows.push({ isGridHeader, rowObject: cellsPerRow });
    });

    const colSpans = colSpansObjects.map(colSpanObject => {
        const { row, title, classNames, colSpanMode, isRightBorder, colSpanIndexes } =
            colSpanObject;

        const colSpansBuilder = {
            [EMPTY]: () => buildEmptyColSpan(row, classNames, colSpanIndexes),
            [GRADE]: () => buildGradeColSpan(isRightBorder, title, colSpanIndexes),
            [MEASURE]: () => buildMeasureColSpan(row, classNames, title, colSpanIndexes),
        };

        return colSpansBuilder[colSpanMode]();
    });

    return { rows, colSpans };
};
